"use client";

import { useEffect, useRef } from "react";

/* ————— v6 Atmosphere —————
   The persistent canvas behind the whole page (Shopify-Editions
   pattern, in our language): the void itself is designed, so there is
   never a dead-black region between sections.

   Two fixed layers, all pointer-events-none, behind content (z-0):
   1. Dust motes — a low-density canvas of drifting gold particles.
      Time-based (not scroll-scrubbed). Paused when the tab is hidden;
      absent under prefers-reduced-motion.
   2. Film grain — a static SVG-noise tile at very low opacity.
   (A scroll-driven "dawn" gradient wash existed in the first cut;
   removed 2026-07-10 per Jay's no-decorative-gradients note. The
   --dawn scroll var is kept in case a non-gradient use appears.)
*/

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

type Mote = {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  phase: number;
  speed: number;
};

export default function Atmosphere() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Scroll → dawn progress (0 at top, 1 at page bottom)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let raf = 0;
    let last = -1;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (Math.abs(p - last) > 0.004) {
        last = p;
        root.style.setProperty("--dawn", p.toFixed(3));
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Dust motes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let motes: Mote[] = [];
    let raf = 0;
    let running = true;
    let w = 0;
    let h = 0;
    let dpr = 1;

    const seed = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(44, (w * h) / 42000));
      motes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.6 + Math.random() * 1.7,
        vx: (Math.random() - 0.5) * 0.08,
        vy: -(0.02 + Math.random() * 0.09),
        phase: Math.random() * Math.PI * 2,
        speed: 0.4 + Math.random() * 0.9,
      }));
    };

    const tick = (t: number) => {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (const m of motes) {
        m.x += m.vx;
        m.y += m.vy;
        if (m.y < -4) {
          m.y = h + 4;
          m.x = Math.random() * w;
        }
        if (m.x < -4) m.x = w + 4;
        if (m.x > w + 4) m.x = -4;
        const twinkle = 0.5 + 0.5 * Math.sin(m.phase + (t / 1000) * m.speed);
        const alpha = 0.05 + twinkle * 0.16;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 163, 62, ${alpha.toFixed(3)})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      running = document.visibilityState === "visible";
      if (running) raf = requestAnimationFrame(tick);
      else if (raf) cancelAnimationFrame(raf);
    };

    seed();
    raf = requestAnimationFrame(tick);
    window.addEventListener("resize", seed);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", seed);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0"
      style={{ ["--dawn" as string]: "0" }}
    >
      {/* Dust motes */}
      <canvas ref={canvasRef} className="absolute inset-0" />
      {/* Film grain */}
      <div
        className="absolute inset-0"
        style={{ backgroundImage: GRAIN, backgroundRepeat: "repeat", backgroundSize: "200px 200px", opacity: 0.05 }}
      />
    </div>
  );
}
